import { computed, ref } from '@vue/composition-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { ITaxData, Tax } from '@/models';
import { MaybeRef } from '@tanstack/vue-query/build/lib/types';
import providerInstance from '@/services/provider';

export function useFetchTaxes() {
  const queryKey = ref(['taxes']);

  const { isLoading, isError, error, data: taxes } = useQuery({
    queryKey: queryKey,
    queryFn: () => providerInstance.tax.fetchTaxes(),
    enabled: true,
    select: data => {
      return data.map(tax => new Tax(tax));
    }
  });

  return { queryKey, taxes, error, isError, isLoading };
}

export function useFetchTax(id: MaybeRef<number>) {
  const taxIdRef = ref(id);

  const queryKey = ref(['taxes', taxIdRef]);

  const { data: tax, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(taxIdRef.value)),
    queryFn: () => providerInstance.tax.fetchTax(taxIdRef.value),
    select: data => new Tax(data)
  });

  return { tax, isLoading, error, queryKey };
}

export function useCreateTax() {
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-tax'],
    mutationFn: (tax: ITaxData) => providerInstance.tax.createTax(tax),

    onSuccess: data => {
      queryClient.setQueryData(['taxes', data.id], data);
      queryClient.invalidateQueries({ queryKey: ref(['taxes']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdateTax() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-tax'],
    mutationFn: (tax: ITaxData) => providerInstance.tax.updateTax(tax),

    onSuccess: data => {
      queryClient.setQueryData(['taxes', data.id], data);
      queryClient.invalidateQueries({
        queryKey: ref(['taxes', data.id])
      });
      queryClient.invalidateQueries({ queryKey: ref(['taxes']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeleteTax() {
  const mutationKey = ref(['delete-tax']);

  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) => providerInstance.tax.deleteTax(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['taxes']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}
